import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useAuth } from "../contexts/AuthContext"
import type { Comment, Photo, Spot, User } from "../types"

const API_URL = import.meta.env.VITE_API_URL || "/api"

type AdminTab = "users" | "comments" | "photos" | "spots"

export default function AdminPage() {
  const { t } = useTranslation()
  const { auth } = useAuth()
  const [activeTab, setActiveTab] = useState<AdminTab>("users")
  const [users, setUsers] = useState<User[]>([])
  const [comments, setComments] = useState<Comment[]>([])
  const [photos, setPhotos] = useState<Photo[]>([])
  const [spots, setSpots] = useState<Spot[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionMsg, setActionMsg] = useState<string | null>(null)

  const headers: Record<string, string> = {}
  if (auth.token) headers["Authorization"] = `Bearer ${auth.token}`

  const fetchData = async (tab: AdminTab) => {
    setLoading(true)
    setError(null)
    try {
      const endpoint =
        tab === "users"
          ? `${API_URL}/admin/users`
          : tab === "comments"
            ? `${API_URL}/admin/comments`
            : tab === "photos"
              ? `${API_URL}/admin/photos`
              : `${API_URL}/admin/spots`
      const res = await fetch(endpoint, { headers })
      if (!res.ok) throw new Error("Failed to load")
      const data = await res.json()
      if (tab === "users") setUsers(data)
      else if (tab === "comments") setComments(data)
      else if (tab === "photos") setPhotos(data)
      else setSpots(data)
    } catch {
      setError(t("error.generic"))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(activeTab)
  }, [activeTab])

  const showAction = (msg: string) => {
    setActionMsg(msg)
    setTimeout(() => setActionMsg(null), 3000)
  }

  const handleBlockUser = async (userId: string, block: boolean) => {
    try {
      const res = await fetch(`${API_URL}/admin/users/${userId}/${block ? "block" : "unblock"}`, {
        method: "POST",
        headers,
      })
      if (!res.ok) throw new Error("Failed")
      const updated: User = await res.json()
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)))
      showAction(block ? t("admin.userBlocked") : t("admin.userUnblocked"))
    } catch {
      setError(t("error.generic"))
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm(t("admin.confirmDeleteComment"))) return
    try {
      const res = await fetch(`${API_URL}/admin/comments/${commentId}`, {
        method: "DELETE",
        headers,
      })
      if (!res.ok) throw new Error("Failed")
      setComments((prev) => prev.filter((c) => c.id !== commentId))
      showAction(t("admin.commentDeleted"))
    } catch {
      setError(t("error.generic"))
    }
  }

  const handleDeletePhoto = async (photoId: string) => {
    if (!confirm(t("admin.confirmDeletePhoto"))) return
    try {
      const res = await fetch(`${API_URL}/admin/photos/${photoId}`, {
        method: "DELETE",
        headers,
      })
      if (!res.ok) throw new Error("Failed")
      setPhotos((prev) => prev.filter((p) => p.id !== photoId))
      showAction(t("admin.photoDeleted"))
    } catch {
      setError(t("error.generic"))
    }
  }

  const handleDeleteSpot = async (spotId: string) => {
    if (!confirm(t("admin.confirmDeleteSpot"))) return
    try {
      const res = await fetch(`${API_URL}/admin/spots/${spotId}`, {
        method: "DELETE",
        headers,
      })
      if (!res.ok) throw new Error("Failed")
      setSpots((prev) => prev.filter((s) => s.id !== spotId))
      showAction(t("admin.spotDeleted"))
    } catch {
      setError(t("error.generic"))
    }
  }

  const tabs: { key: AdminTab; label: string }[] = [
    { key: "users", label: t("admin.tabUsers") },
    { key: "comments", label: t("admin.tabComments") },
    { key: "photos", label: t("admin.tabPhotos") },
    { key: "spots", label: t("admin.tabSpots") },
  ]

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-950">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t("admin.title")}</h2>
      </div>

      <div className="flex gap-1 px-4 pb-3 border-b border-gray-200 dark:border-slate-800 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.key
                ? "bg-steel text-white"
                : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {actionMsg && (
        <div className="mx-4 mt-3 p-3 rounded-xl bg-forest/10 text-forest-dark dark:text-forest-light text-sm font-medium">
          {actionMsg}
        </div>
      )}

      {error && (
        <div className="mx-4 mt-3 p-3 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">{t("loading")}</p>
        ) : (
          <>
            {activeTab === "users" && (
              <div className="space-y-3">
                {users.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    {t("admin.noUsers")}
                  </p>
                )}
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="bg-gray-50 dark:bg-slate-800 rounded-xl p-4 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {user.username}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                      <div className="flex gap-2 mt-1">
                        {user.is_admin && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium">
                            {t("admin.adminBadge")}
                          </span>
                        )}
                        {user.is_blocked && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-medium">
                            {t("admin.blockedBadge")}
                          </span>
                        )}
                      </div>
                    </div>
                    {user.id !== auth.user?.id && (
                      <button
                        onClick={() => handleBlockUser(user.id, !user.is_blocked)}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          user.is_blocked
                            ? "bg-forest/10 text-forest-dark dark:text-forest-light hover:bg-forest/20"
                            : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50"
                        }`}
                      >
                        {user.is_blocked ? t("admin.unblock") : t("admin.block")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === "comments" && (
              <div className="space-y-3">
                {comments.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    {t("admin.noComments")}
                  </p>
                )}
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-gray-50 dark:bg-slate-800 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-steel text-white flex items-center justify-center text-xs font-bold shrink-0">
                          {(comment.username || "A").charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {comment.username || t("spot.anonymous")}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="shrink-0 text-red-400 hover:text-red-600 text-sm p-1"
                        title={t("admin.delete")}
                      >
                        🗑️
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{comment.text}</p>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "photos" && (
              <div className="space-y-3">
                {photos.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    {t("admin.noPhotos")}
                  </p>
                )}
                {photos.map((photo) => (
                  <div
                    key={photo.id}
                    className="bg-gray-50 dark:bg-slate-800 rounded-xl p-3 flex items-center gap-3"
                  >
                    <img
                      src={photo.url}
                      alt={photo.caption || ""}
                      className="w-16 h-16 rounded-lg object-cover shrink-0 bg-gray-200 dark:bg-slate-700"
                      loading="lazy"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                        {photo.caption || t("spot.photos")}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(photo.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="shrink-0 text-red-400 hover:text-red-600 text-sm p-1"
                      title={t("admin.delete")}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "spots" && (
              <div className="space-y-3">
                {spots.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    {t("admin.noSpots")}
                  </p>
                )}
                {spots.map((spot) => (
                  <div
                    key={spot.id}
                    className="bg-gray-50 dark:bg-slate-800 rounded-xl p-4 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {spot.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {spot.category} · {spot.rating_count} ratings
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteSpot(spot.id)}
                      className="shrink-0 text-red-400 hover:text-red-600 text-sm p-1"
                      title={t("admin.delete")}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
